
import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import "@fullcalendar/core/vdom"; // solves problem with Vite
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment, { Moment } from "moment";
import { WeeklyEvent, MonthlyEvent, DayEvent, BusinessHours } from "@/@types/event";
import CalendarModule from "@/store/calendar";
import FacilityModule from "@/store/facility";
import BirthdaySelect from "@/components/forms/components/BirthdaySelect.vue";
import FacilityService from "@/services/facilities";
import { ReservationData } from "@/@types/reservation";
import { CourseData, CourseDay, CourseDetail, CourseSpecialTime } from "@/@types/course";
import { asyncMap, checkHoliday, deleteNullProp, asyncForEach } from "@/utils/Util";
import _ from "lodash";
import Encoding from "encoding-japanese";
import Papa from "papaparse";
import Button from "@/components/Button.vue";
import FormSet from "@/components/FormSet.vue";
import AllCourseCalendar from "./AllCourseCalendar.vue";
import DayCalendar from "./DayCalendar.vue";
import { uploadToS3ForCourse } from "@/plugins/s3";

@Component({
  components: {
    FullCalendar,
    BirthdaySelect,
    Button,
    FormSet,
    AllCourseCalendar,
    DayCalendar,
  },
})
export default class Home extends Vue {
  @Ref() fullCalendar!: any;

  private showCalendarDialog = false;
  private calendarKey = 0;

  selectedDates: Date[] = [];
  private holidays: string[] = [];

  // 全コース選択時に True
  private get isAllCourseSelected(): boolean {
    return this.selectedCourse?.id === "all";
  }

  // Backlog#1151: コース表示カスタマイズ用 getter 群
  // 選択中のコースID配列（Vuexから取得）
  private get selectedCourseIds(): string[] {
    return CalendarModule.selectedCourseIds;
  }

  // 「全コース」を除いたコース配列（コース設定順）
  private get exceptAllCourses(): CourseData[] {
    return this.courses.filter((c) => c.id !== "all");
  }

  // Backlog#1151: selectedCourseIds のうち「今実在するコース」だけに絞り込んだ配列
  // コース設定側で削除されたコースのIDが Vuex/localStorage に残ってしまうケースがあり、
  // その残留IDを除外することで、以下の不整合を防ぐ：
  //   - 適用ボタンの活性/非活性判定が狂う（残留IDのせいで 0件のはずが 1件扱いになる等）
  //   - 5件上限判定が狂う（残留IDのせいで 4件のはずが 5件扱いになる等）
  //   - displayMode 判定が狂う（残留IDのせいで single のはずが multi 扱いになる等)
  private get validSelectedCourseIds(): string[] {
    const ids = this.selectedCourseIds;
    if (ids.includes("all")) return ["all"];
    return ids.filter((id) => this.exceptAllCourses.some((c) => c.id === id));
  }

  // 現在の表示モード
  // - "all"    : 全コース選択（先頭5件を ←/→ で切替）
  // - "multi"  : 複数コース選択（2〜5件をコース設定順で並べる）
  // - "single" : 単体コース選択（1件のみ、週/月表示可）
  private get displayMode(): "all" | "multi" | "single" {
    const ids = this.validSelectedCourseIds;
    if (ids.includes("all")) return "all";
    if (ids.length >= 2) return "multi";
    return "single";
  }

  // トリガー要素に表示するラベル文字列
  private get courseSelectionLabel(): string {
    const mode = this.displayMode;
    if (mode === "all") return "全コース";
    if (mode === "multi") return "複数コース選択中";
    const course = this.exceptAllCourses.find((c) => c.id === this.validSelectedCourseIds[0]);
    return course?.name ?? "";
  }

  // Backlog#1151: コース選択ポップオーバー開閉制御
  private isCourseSelectorPopoverOpen = false;
  private courseSelectorPopoverStyle: { left: string; top: string; width: string } = {
    left: "0px",
    top: "0px",
    width: "auto",
  };
  // ポップオーバー内の一時的な選択状態（適用ボタン押下まで確定しない）
  private tempSelectedIds: string[] = ["all"];

  private toggleCourseSelectorPopover(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isCourseSelectorPopoverOpen) {
      this.closeCourseSelectorPopover();
      return;
    }
    // 開くときに現在の確定状態から一時状態を初期化（Backlog#1151: 残留IDを除外）
    this.tempSelectedIds = [...this.validSelectedCourseIds];
    this.isCourseSelectorPopoverOpen = true;
    // DOM 描画後に座標を計算
    this.$nextTick(() => this.positionCourseSelectorPopover());
  }

  // チェックボックス操作ハンドラ（排他制御 + 5件上限）
  private onCourseCheckboxChange(courseId: string, checked: boolean): void {
    const isAllCourse = courseId === "all";
    let next = [...this.tempSelectedIds];

    if (checked) {
      if (isAllCourse) {
        // 「全コース」チェック → 他を全解除して「全コース」のみに
        next = ["all"];
      } else {
        // 他コースチェック → 「全コース」を外す。5件超過はガード
        next = next.filter((id) => id !== "all");
        if (next.length >= 5) return;
        next.push(courseId);
      }
    } else {
      // 0件状態は許容（適用ボタン側で確定を抑止する予定）
      next = next.filter((id) => id !== courseId);
    }

    this.tempSelectedIds = next;
  }

  // 各コースのチェックボックスが disabled かどうか
  // 排他制御はチェック操作時に onCourseCheckboxChange 側で行うため、
  // ここでは 5件上限の 6件目防止のみを担当する。
  private isCourseCheckboxDisabled(courseId: string): boolean {
    // 5件到達時は、未選択の他コースをグレーアウト（6件目防止）
    if (this.tempSelectedIds.length >= 5 && !this.tempSelectedIds.includes(courseId) && courseId !== "all") {
      return true;
    }
    return false;
  }

  // 「適用」ボタンが disabled かどうか（0件では確定できない・Q10）
  private get isCourseApplyDisabled(): boolean {
    return this.tempSelectedIds.length === 0;
  }

  // 「クリア」ボタン：Q5案A（即反映） 全コース選択に戻して閉じる
  private onClearCourseSelector(): void {
    this.commitSelectedCourseIds(["all"]);
    this.closeCourseSelectorPopover();
  }

  // 「適用」ボタン：一時状態を確定してポップオーバーを閉じる
  // Backlog#1151: 別タブ/別端末での削除後にポップオーバー内で残留IDのまま
  // 適用されると Vuex に残留IDが書き戻ってしまうため、実在するコースだけに絞ってから確定する。
  private onApplyCourseSelector(): void {
    if (this.isCourseApplyDisabled) return;
    const cleaned = this.tempSelectedIds.includes("all")
      ? ["all"]
      : this.tempSelectedIds.filter((id) => this.exceptAllCourses.some((c) => c.id === id));
    this.commitSelectedCourseIds(cleaned);
    this.closeCourseSelectorPopover();
  }

  // selectedCourseIds を確定して既存のカレンダー描画フローに乗せる
  // 既存の selectedCourse に依存する箇所を壊さないため、モード③(単体)は該当コース、
  // それ以外は全コース(id: "all")を selectedCourse にセットする。
  // モード①②は週/月表示不可のため timeGridDay を強制。
  // 最後に location.reload() で既存の初期化フローを走らせる（既存の changeCourse と同じパターン）。
  private commitSelectedCourseIds(ids: string[]): void {
    CalendarModule.changeSelectedCourseIds(ids);
    const isSingle = !ids.includes("all") && ids.length === 1;
    if (isSingle) {
      const single = this.exceptAllCourses.find((c) => c.id === ids[0]);
      if (single) CalendarModule.changeSelectedCourse(single);
    } else {
      const allCourse = this.courses.find((c) => c.id === "all");
      if (allCourse) CalendarModule.changeSelectedCourse(allCourse);
      // モード①②は週/月不可なので日表示に強制
      CalendarModule.changeSelectedInitialView("timeGridDay");
    }
    // Backlog#1151: リロードを常に1回に揃えるための制御。
    // FullCalendar の calendarOptions は初期化時に Vuex の営業時間（businessHours）を読むため、
    // 単体モードではリロード前に新しいコースの営業時間を Vuex に反映しておく必要がある
    // （反映せずにリロードすると、直前のコースの営業時間で週/月表示が描画されてしまう）。
    // businessHoursArrayFactory は isReloaded === false のとき内部で reload してしまうので、
    // 先に true にして内部 reload を抑止し、営業時間の書き込みだけ行わせる。
    if (isSingle) {
      CalendarModule.changeIsReloaded(true);
      this.businessHoursArrayFactory(); // 営業時間を Vuex に反映（else 分岐に入り isReloaded は false に戻る）
    }
    // リロード後の mounted で再度 businessHoursArrayFactory が呼ばれても追加リロードしないよう true にしてから reload
    CalendarModule.changeIsReloaded(true);
    location.reload();
  }

  private closeCourseSelectorPopover(): void {
    this.isCourseSelectorPopoverOpen = false;
  }

  // トリガー要素の真下を基本に、画面内に収まるよう座標を計算（予約一覧の positionPopover を踏襲）
  private positionCourseSelectorPopover(): void {
    const trigger = this.$refs.courseSelectorTrigger as HTMLElement | undefined;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    // ポップオーバー幅はトリガー要素と同じにする（Backlog#1151）
    const popWidth = rect.width;
    const popEl = document.querySelector(".course-selector-popover") as HTMLElement | null;
    const popHeight = popEl?.offsetHeight ?? 300;
    const margin = 8;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    let left = rect.left;
    if (left + popWidth + margin > vw) {
      left = Math.max(margin, vw - popWidth - margin);
    }
    let top = rect.bottom + 4;
    if (top + popHeight + margin > vh) {
      const above = rect.top - popHeight - 4;
      top = above >= margin ? above : Math.max(margin, vh - popHeight - margin);
    }
    this.courseSelectorPopoverStyle = { left: `${left}px`, top: `${top}px`, width: `${popWidth}px` };
  }

  // 外側クリック・スクロール・リサイズで閉じる（予約一覧の onDocumentClick を踏襲）
  private onCourseSelectorDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest(".course-selector-popover")) return;
    if (target.closest(".course-selector-trigger")) return;
    this.closeCourseSelectorPopover();
  };
  // スクロール/リサイズで閉じる。ただしポップオーバー内部のスクロールは無視する
  private onCourseSelectorWindowChange = (e?: Event) => {
    const target = e?.target as Node | Element | null;
    if (target && "closest" in (target as Element) && (target as Element).closest?.(".course-selector-popover")) {
      return;
    }
    this.closeCourseSelectorPopover();
  };

  private registerCourseSelectorListeners(): void {
    document.addEventListener("click", this.onCourseSelectorDocumentClick);
    window.addEventListener("scroll", this.onCourseSelectorWindowChange, true);
    window.addEventListener("resize", this.onCourseSelectorWindowChange);
  }

  private unregisterCourseSelectorListeners(): void {
    document.removeEventListener("click", this.onCourseSelectorDocumentClick);
    window.removeEventListener("scroll", this.onCourseSelectorWindowChange, true);
    window.removeEventListener("resize", this.onCourseSelectorWindowChange);
  }

  beforeDestroy(): void {
    this.unregisterCourseSelectorListeners();
    this.closeCourseSelectorPopover();
  }

  // 予約枠初期化モーダル表示フラグ
  private showResetReservationDialog = false;
  // 予約枠ゼロ化モーダル表示フラグ
  private showClearReservationDialog = false;
  // 初期化対象の日付（Date型またはISO文字列）
  selectedDatesToReset: (string | Date)[] = [];

  // Date型を ISO形式（YYYY-MM-DD）に変換する関数
  private formatDateToJST(date: Date): string {
    const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000); // JST補正
    return jst.toISOString().split("T")[0];
  }

  // 本日日付を取得
  private todayJST = this.formatDateToJST(new Date());

  // 予約枠一括変換ボタンを押した時のみ（確認モーダルからの遷移時は起動しない）
  private async openCalendarDialog(): Promise<void> {
    this.loadingCalendar = true;
    // holidays を API から取得
    this.holidays = await this.getYearlyClosedDates();
    // console.log("holidays:", this.holidays);

    // カレンダーモーダル初期表示を本日日付の年月に固定
    this.calendarKey++;
    this.loadingCalendar = false;
    this.showCalendarDialog = true;
  }

  private get minDate() {
    const now = new Date();
    // JSTに変換（UTC+9時間）
    now.setHours(now.getHours() + 9);
    return now.toISOString().split("T")[0];
  }

  private get maxDate() {
    const now = new Date();
    // JSTに変換（UTC+9時間）
    now.setHours(now.getHours() + 9);
    // 翌年の今月末に設定
    const nextYear = now.getFullYear() + 1;
    const thisMonth = now.getMonth(); // 今の月（0-indexed）
    const endOfMonth = new Date(nextYear, thisMonth + 1, 0); // 翌年の今月末日

    // JST形式で 'YYYY-MM-DD' を返す
    const yyyy = endOfMonth.getFullYear();
    const mm = String(endOfMonth.getMonth() + 1).padStart(2, "0");
    const dd = String(endOfMonth.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  }

  private get calendarAttributes() {
    return [
      {
        key: "holiday",
        dates: this.holidays,
        highlight: {
          class: "holiday-date",
        },
        // クリック不可にする
        customData: { disabled: true },
      },
      {
        key: "today",
        dates: [this.todayJST],
        highlight: {
          class: "today-date",
        },
      },
      {
        key: "selected",
        dates: this.selectedDates,
        highlight: {
          class: "selected-date",
        },
      },
    ];
  }

  private toggleDate(day: { date: Date }) {
    // 休診日は選択不可
    const dateISO = this.formatDateToJST(day.date);
    if (this.holidays.includes(dateISO)) {
      return;
    }

    // 過去日は選択不可
    const clickedDateJST = this.formatDateToJST(day.date);
    if (clickedDateJST < this.todayJST) return;

    const dateStr = day.date.toDateString();
    const index = this.selectedDates.findIndex((d) => d.toDateString() === dateStr);
    if (index >= 0) {
      this.selectedDates.splice(index, 1);
    } else {
      this.selectedDates.push(day.date);
    }
  }

  // 日付の表示整形
  formatDateWithWeekdayJp(date: string | Date): string {
    const d = new Date(date);
    return d.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "short",
    });
  }

  // ボタンクリックで予約枠初期化モーダルを開く
  openResetReservationDialog(): void {
    // 昇順にソートしてからセットする
    this.selectedDatesToReset = [...this.selectedDates].sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });
    this.showResetReservationDialog = true;
    this.showCalendarDialog = false;
  }

  // 予約枠初期化実行
  private async confirmResetReservation(): Promise<void> {
    // 予約枠初期化処理
    this.resetReservation();

    // 選択状態をクリア
    this.selectedDates = [];
    this.selectedDatesToReset = [];

    // this.holidays = await this.getMonthsClosedDates();
    // console.log("holidays:", this.holidays);

    // モーダルを閉じる
    this.showResetReservationDialog = false;
    this.showCalendarDialog = false;
  }

  // 予約枠初期化処理
  private async resetReservation(): Promise<void> {
    if (!this.hospitalId || !this.selectedCourse) return;

    const updatedCourse = _.cloneDeep(this.selectedCourse);

    // コースごとの available_count を取得
    const defaultAvailableCount = updatedCourse.available_count;

    // Date型 → 'YYYY-MM-DD'文字列へ変換
    const isoDateStrings = this.selectedDates.map((d) => this.formatDateToJST(d));
    console.log("isoDateStrings:", isoDateStrings);

    for (const detail of updatedCourse.details) {
      if (!detail.special_times) continue;

      for (const dateStr of isoDateStrings) {
        const target = moment(dateStr);
        const year = target.year();
        const month = target.month() + 1;
        const date = target.date();

        detail.special_times.forEach((time) => {
          if (time.year === year && time.month === month && time.date === date) {
            time.available_count = defaultAvailableCount;
          }
        });
      }
    }

    const payload = deleteNullProp(updatedCourse);
    console.log("resetReservation payload:", payload);

    try {
      const res = await FacilityService.updateCourse(this.hospitalId, this.selectedCourse.id, payload);
      CalendarModule.changeSelectedCourse(res.data.data[0]);
      if (this.selectedInitialView === "timeGridDay") {
        // DayCalendar の再描画
        this.showChildComponent = false;
        await this.$nextTick();
        this.showChildComponent = true;
      }
      if (this.selectedInitialView === "timeGridWeek") {
        this.createWeeklyStock();
      }
      if (this.selectedInitialView === "dayGridMonth") {
        this.createMonthlyStock();
      }
    } catch (e) {
      console.error("resetReservation failed:", e);
    }
    console.log("予約枠を初期化しました。");
  }

  // ボタンクリックで予約枠ゼロ化モーダルを開く
  openClearReservationDialog(): void {
    // 昇順にソートしてからセットする
    this.selectedDatesToReset = [...this.selectedDates].sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });
    this.showClearReservationDialog = true;
    this.showCalendarDialog = false;
  }

  // 予約枠ゼロ化実行
  private async confirmClearReservation(): Promise<void> {
    // 予約枠ゼロ化処理
    this.clearReservation();

    // 選択状態をクリア
    this.selectedDates = [];
    this.selectedDatesToReset = [];

    // this.holidays = await this.getMonthsClosedDates();
    // console.log("holidays:", this.holidays);

    // モーダルを閉じる
    this.showClearReservationDialog = false;
    this.showCalendarDialog = false;
  }

  private async clearReservation(): Promise<void> {
    if (!this.hospitalId || !this.selectedCourse) return;

    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const clearCourse = _.cloneDeep(this.selectedCourse);
    // 継続時間（分）取得
    const duration = clearCourse.duration_minute;

    for (const detail of clearCourse.details) {
      // コース開始日取得
      const periodStart = moment({
        year: detail.period.start_year,
        month: detail.period.start_month - 1,
        day: detail.period.start_date,
      });

      // コース終了日取得（終了日設定無しを考慮）
      const periodEnd =
        detail.period.end_year && detail.period.end_month && detail.period.end_date
          ? moment({
              year: detail.period.end_year,
              month: detail.period.end_month - 1,
              day: detail.period.end_date,
              hour: 23,
              minute: 59,
              second: 59,
            })
          : null;

      // 指定された selectedDates から期間内だけにフィルターする
      const filteredDates = this.selectedDates.filter((d) => {
        const m = moment(this.formatDateToJST(d));
        return m.isSameOrAfter(periodStart) && (!periodEnd || m.isSameOrBefore(periodEnd));
      });

      for (const date of filteredDates) {
        // Date型 → 'YYYY-MM-DD'文字列へ変換
        const isoDate = this.formatDateToJST(date);
        const target = moment(isoDate);
        const year = target.year();
        const month = target.month() + 1;
        const day = target.date();
        const dow = weekdays[target.day()];

        // 祝日判定
        const isHoliday = checkHoliday(target.toDate()); // japanese-holidays を利用（true/false）
        // matchedDay を初期化
        let matchedDay: any | undefined;

        if (isHoliday) {
          // 祝日の場合 matchedDay にするのは次の3パターン
          // 1) 最優先：祝日ボタン=ON かつ 祝日設定を優先する=ON
          //    → 祝日は特定の診療時間で受診したい場合（曜日を分けるパターン）
          //    → d.available_days に "Holiday" を含み、かつ d.is_holiday_primary === true にマッチ
          // 2) 祝日ボタン=OFF かつ 祝日設定を優先する=OFF
          //    → 月〜日の診療時間と同じにしたい場合（曜日を分けないパターン）
          //    → d.available_days に "Holiday" は含まず、かつ d.is_holiday_primary === false、かつ通常の曜日(dow) にマッチ
          // 3) 祝日ボタン=ON かつ 祝日設定を優先する=OFF
          //    → 祝日の診療時間を設定しているが、通常曜日と同じ扱いにする場合
          //    → d.available_days に "Holiday" を含み、かつ d.is_holiday_primary === false にマッチ
          // 対象外のパターン
          // ・祝日ボタン=OFF かつ 祝日設定を優先する=ON
          //    → 祝日は休診にしたい場合（曜日を分けないパターン）
          matchedDay =
            // ON/ON パターン
            detail.days?.find((d: any) => d.is_holiday_primary === true && d.available_days?.includes("Holiday")) ??
            // OFF/OFF パターン
            detail.days?.find(
              (d: any) =>
                d.is_holiday_primary !== true &&
                !d.available_days?.includes("Holiday") &&
                d.available_days?.includes(dow)
            ) ??
            // ON/OFF パターン
            detail.days?.find((d: any) => d.is_holiday_primary !== true && d.available_days?.includes("Holiday"));
          if (!matchedDay) continue;
        } else {
          // 祝日でない場合、weekday_days にマッチする days を取得
          matchedDay = detail.days.find((d) => d.available_days.includes(dow));
          // available_days に無い曜日の場合、この日のクリアは対象外
          if (!matchedDay) continue;
        }

        for (const time of matchedDay.times) {
          const { start_hour, start_minute, end_hour, end_minute } = time.on_time;

          // 時間帯ループ（duration_minute 単位）
          let start = moment({ year, month: month - 1, day, hour: start_hour, minute: start_minute });
          const end = moment({ year, month: month - 1, day, hour: end_hour, minute: end_minute });

          while (start.isBefore(end)) {
            const specialTime = {
              year,
              month,
              date: day,
              start_hour: start.hour(),
              start_minute: start.minute(),
              available_count: 0,
            };

            // すでに同じ時間の special_time があるか確認
            const existing = detail.special_times?.find(
              (s) =>
                s.year === specialTime.year &&
                s.month === specialTime.month &&
                s.date === specialTime.date &&
                s.start_hour === specialTime.start_hour &&
                s.start_minute === specialTime.start_minute
            );
            if (existing) {
              existing.available_count = 0;
            } else {
              if (!detail.special_times) detail.special_times = [];
              detail.special_times.push(specialTime);
            }

            start = start.clone().add(duration, "minutes");
          }
        }
      }
    }

    const payload = deleteNullProp(clearCourse);
    console.log("clearReservation payload:", payload);

    try {
      const res = await FacilityService.updateCourse(this.hospitalId, this.selectedCourse.id, payload);
      CalendarModule.changeSelectedCourse(res.data.data[0]);
      if (this.selectedInitialView === "timeGridDay") {
        // DayCalendar の再描画
        this.showChildComponent = false;
        await this.$nextTick();
        this.showChildComponent = true;
      }
      if (this.selectedInitialView === "timeGridWeek") {
        this.createWeeklyStock();
      }
      if (this.selectedInitialView === "dayGridMonth") {
        this.createMonthlyStock();
      }
    } catch (e) {
      console.error("clearReservation failed:", e);
    }
    console.log("予約枠をゼロにしました。");
  }

  // カレンダーモーダルのキャンセルボタン
  cancelCalendarDialog(): void {
    // 選択状態をクリア
    this.selectedDates = [];
    this.selectedDatesToReset = [];

    // モーダルを閉じる
    this.showCalendarDialog = false;
  }

  // 予約枠初期化モーダルのキャンセルボタン
  cancelResetReservationDialog(): void {
    // モーダルを閉じる
    this.showResetReservationDialog = false;
    this.showCalendarDialog = true;
  }

  // 予約枠ゼロ化モーダルのキャンセルボタン
  cancelClearReservationDialog(): void {
    // モーダルを閉じる
    this.showClearReservationDialog = false;
    this.showCalendarDialog = true;
  }

  private formatJapaneseTitle(title: string): string {
    // 「7月 2025」や「7月2025」形式を想定して正規表現で抽出
    const match = title.match(/(\d{1,2})月\s*(\d{4})/);
    if (match) {
      const [, month, year] = match;
      return `${year}年${month}月`;
    }
    return title; // フォールバック
  }

  // private get formattedDateList(): string[] {
  //   return this.selectedDates.map((d) => d.toISOString().split("T")[0].replace(/-/g, ""));
  // }
  private get formattedDateList(): string[] {
    return this.selectedDates.map((d) => {
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const day = d.getDate().toString().padStart(2, "0");
      return `${year}${month}${day}`;
    });
  }

  private weeklyHeight = 60;

  private get currentEvents() {
    return CalendarModule.currentEvents;
  }

  private get hospitalId() {
    return FacilityModule.hospitalId;
  }

  private get monthlyEvents() {
    return CalendarModule.monthlyEvents;
  }
  private get selectedInitialView() {
    return CalendarModule.selectedInitialView;
  }

  private get weeklyEvents() {
    return CalendarModule.weeklyEvents;
  }
  private get businessHours() {
    return CalendarModule.businessHours;
  }
  private get businessHoursForSetHeightCalendar() {
    return CalendarModule.businessHoursForSetHeightCalendar;
  }
  private get isReloaded() {
    return CalendarModule.isReloaded;
  }

  private get selectedTargetTime() {
    return CalendarModule.selectedTargetTime;
  }
  private get selectedCourse() {
    return CalendarModule.selectedCourse;
  }

  private canClickCalendarButtons = true;

  // private get reservations() {
  //   return CalendarModule.reservations;
  // }

  private reservations: any = [];
  private groupReservations: any = [];

  private get currentDate() {
    return CalendarModule.currentDate;
  }

  private get totalSpecialTimes() {
    let count = 0;
    for (const detail of this.selectedCourse?.details || []) {
      if (detail.special_times?.length) {
        for (const item of detail.special_times) {
          count += Math.abs(item.available_count - (this.selectedCourse?.available_count || 0));
        }
      }
    }
    return count;
  }

  private loading = false;
  private loadingCalendar = false;

  @Watch("loading")
  private handleLoadingReservations(newVal: boolean) {
    if (newVal) {
      this.canClickCalendarButtons = false;
    } else {
      setTimeout(() => {
        this.canClickCalendarButtons = true;
      }, 1000);
    }
  }

  private loadingUpdateCourse = false;

  private canUpdateCourse = true;

  @Watch("loadingUpdateCourse")
  private handleLoadingUpdateCourse(newVal: boolean) {
    if (newVal) {
      this.canUpdateCourse = false;
    } else {
      setTimeout(() => {
        this.canUpdateCourse = true;
      }, 1000);
    }
  }

  private showChildComponent = false;

  private courses: CourseData[] = [];

  private allCourseReservations: any = [];

  private possibleReservations: any = [];

  // private isEdit = false;

  private csvDownloading = false;
  private getAllCourseReservations(data: any) {
    this.allCourseReservations = data;
  }

  private toNowDate() {
    CalendarModule.changeCurrentDate(moment(new Date()).format("YYYY-MM-DD"));
    location.reload();
  }

  private changeCurrentDate(date: Date) {
    const dateMoment = moment(date);
    // 時間を00:00にセット
    dateMoment.set("hour", 0);
    dateMoment.set("minute", 0);
    dateMoment.set("second", 0);
    CalendarModule.changeCurrentDate(moment(dateMoment.toDate()).format("YYYY-MM-DD"));
    window.location.reload();
    if (this.currentDate === "Invalid date") {
      CalendarModule.changeCurrentDate("");
    }
  }

  private async getCourses() {
    try {
      const res = await FacilityService.getCourses(this.hospitalId);
      this.courses = [
        {
          name: "全コース",
          id: "all",
          duration_minute: 30,
          available_count: 1,
          details: [{}],
          facility_id: "all",
          sort_index: 0,
        },
        ...res.data.data,
      ];

      if (!this.selectedCourse && this.courses.length) {
        this.changeCourse(this.courses[0]);
      }

      // Backlog#1151: 別タブ/別端末でコース削除された結果、
      // Vuex の selectedCourseIds / selectedCourse が実在しないIDを参照している場合に自己修復する。
      // 残留IDを除外した上で、displayMode に応じて selectedCourse も再設定することで、
      // AllCourseCalendar / DayCalendar の切替が実態と整合するようにする。
      this.syncSelectedCourseIdsWithCourses();

      // Backlog#1151: 全コース表示で ←/→ で2ページ目以降を表示中に、
      // コース設定側でコースが削除されて viewCourseIndex が範囲外になった場合の補正。
      // 例：7件あり viewCourseIndex=5 で 6・7件目を表示中に、1〜3件目を削除して
      // 総数が 4件に減ると、viewCourseIndex=5 のままだと空表示になり ←/→ ボタンも
      // 消えて戻れなくなる。範囲外なら 0 にリセットして先頭ページに戻す。
      this.syncViewCourseIndexWithCourses();
    } catch (error: any) {
      throw new Error(error);
    }
  }

  // Backlog#1151: 実在するコースのみに selectedCourseIds を絞り込み、
  // selectedCourse も現在のモードに合わせて再設定する自己修復処理
  private syncSelectedCourseIdsWithCourses(): void {
    const rawIds = this.selectedCourseIds;
    if (rawIds.includes("all")) return; // 全コース選択中は何もしない

    const cleanedIds = rawIds.filter((id) => this.exceptAllCourses.some((c) => c.id === id));

    if (cleanedIds.length === rawIds.length) {
      // 残留IDなし・selectedCourse も含めて整合済み想定なので何もしない
      return;
    }

    if (cleanedIds.length === 0) {
      // 全て消えた → 全コースに戻す
      CalendarModule.changeSelectedCourseIds(["all"]);
      const allCourse = this.courses.find((c) => c.id === "all");
      if (allCourse) CalendarModule.changeSelectedCourse(allCourse);
      // Backlog#1151: 週/月表示中に単体コースが削除された場合、
      // 全コース選択に戻すと同時に日表示にも切り替える必要がある（全コースは日表示のみ対応のため）。
      // calendarOptions.initialView は既にクラスプロパティ初期化時に評価済みで、
      // ここで Vuex を書き換えても FullCalendar 側の内部状態は週/月のまま矛盾するため、
      // location.reload() で FullCalendar を初期化し直す（無限ループ回避のため日表示中は何もしない）。
      if (this.selectedInitialView !== "timeGridDay") {
        CalendarModule.changeSelectedInitialView("timeGridDay");
        location.reload();
      }
      return;
    }

    CalendarModule.changeSelectedCourseIds(cleanedIds);
    if (cleanedIds.length === 1) {
      // 単体モード → selectedCourse を該当コースに合わせる
      const single = this.exceptAllCourses.find((c) => c.id === cleanedIds[0]);
      if (single) CalendarModule.changeSelectedCourse(single);
    }
    // 複数モードの場合 selectedCourse は全コースオブジェクトのまま（AllCourseCalendar を維持）
  }

  // Backlog#1151: viewCourseIndex が exceptAllCourses の範囲外になった場合に先頭に戻す
  // 全コース表示で ←/→ による2ページ目以降表示中にコースが削除されると発生する
  private syncViewCourseIndexWithCourses(): void {
    const idx = CalendarModule.viewCourseIndex;
    if (idx >= this.exceptAllCourses.length) {
      CalendarModule.changeViewCourseIndex(0);
    }
  }
  // 予約一覧画面へ遷移（Backlog-1139）
  // 「日」表示で開いている日付を引き継いで、当日分の予約のみ初期表示する
  private toReservationList() {
    const date = moment(this.currentDate).format("YYYY-MM-DD");
    this.$router.push({ name: "ReservationList", query: { date } });
  }

  private async changeCourse(course: CourseData) {
    CalendarModule.changeSelectedCourse(course);
    // Backlog#1151: selectedCourseIds も同期させる
    CalendarModule.changeSelectedCourseIds(course.id === "all" ? ["all"] : [course.id]);
    if (course.id === "all") {
      CalendarModule.changeSelectedInitialView("timeGridDay");
    }
    if (this.selectedInitialView !== "timeGridDay") {
      this.calendarHeaderDisabled();
      this.businessHoursArrayFactory();
      this.getReservations();
    } else {
      CalendarModule.changeSelectedInitialView("timeGridDay");
    }
    if (this.selectedInitialView === "timeGridWeek") {
      CalendarModule.changeCurrentDate(
        moment(this.fullCalendar.getApi().currentData.viewApi.currentStart).format("YYYY-MM-DD")
      );
    }
    location.reload();
  }

  private async getReservations() {
    await this.getPossibleReservations();
    this.calendarHeaderDisabled();
    let reservations: ReservationData[] = [];
    let groupReservations: ReservationData[] = [];
    let groupCourseIds: string[] = [];
    const courseGroupsRes = await FacilityService.getCourseGroups(this.hospitalId);
    const courseGroups = courseGroupsRes.data.data;
    // courseGroups(配列)の中にあるcourse_idsに選択中のコースIDがあればそのコースグループを取得
    const currentCourseGroup = courseGroups.find((courseGroup: any) =>
      courseGroup.course_ids?.includes(this.selectedCourse!.id)
    );
    if (currentCourseGroup) {
      // コースグループの中にあるコースIDから現在のコースIDを除外
      groupCourseIds = currentCourseGroup.course_ids?.filter(
        (courseId: string) => courseId !== this.selectedCourse!.id
      );
    }
    const calendarStartDate = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart);
    const calendarEndDate = moment(this.fullCalendar.getApi().currentData.viewApi.currentEnd);
    const startYear = calendarStartDate.get("year").toString();
    const startMonth = String(calendarStartDate.get("month") + 1);
    const endYear = calendarEndDate.get("year").toString();
    const endMonth = String(calendarEndDate.get("month") + 1);
    const res = await FacilityService.getReservations(this.hospitalId, {
      year: startYear,
      month: startMonth,
      course_id: this.selectedCourse!.id,
    });
    reservations = res.data.data;
    await asyncForEach(groupCourseIds, async (courseId: string) => {
      const res = await FacilityService.getReservations(this.hospitalId, {
        year: startYear,
        month: startMonth,
        course_id: courseId,
      });
      groupReservations = [...groupReservations, ...res.data.data];
    });
    if (this.selectedInitialView === "timeGridWeek" || this.selectedInitialView === "dayGridMonth") {
      reservations = reservations.filter(
        (reservation: ReservationData) => reservation.data_type !== "reservation_special"
      );
      groupReservations = groupReservations.filter(
        (reservation: ReservationData) => reservation.data_type !== "reservation_special"
      );
    }
    if (endMonth !== startMonth) {
      const res = await FacilityService.getReservations(this.hospitalId, {
        year: endYear,
        month: endMonth,
        course_id: this.selectedCourse!.id,
      });
      reservations = [...reservations, ...res.data.data];
      await asyncForEach(groupCourseIds, async (courseId: string) => {
        const res = await FacilityService.getReservations(this.hospitalId, {
          year: endYear,
          month: endMonth,
          course_id: courseId,
        });
        groupReservations = [...groupReservations, ...res.data.data];
      });
    }
    const addStartAndEndTime = await asyncMap(reservations, async (reservation: ReservationData) => {
      return {
        ...reservation,
        start: moment(reservation.datetime).format(),
        end: moment(reservation.datetime).add(this.selectedCourse?.duration_minute, "minute").format(),
        course: this.selectedCourse,
      };
    });

    this.reservations = addStartAndEndTime;
    this.groupReservations = await asyncMap(groupReservations, async (reservation: ReservationData) => {
      return {
        ...reservation,
        start: moment(reservation.datetime).format(),
        end: moment(reservation.datetime).add(this.selectedCourse?.duration_minute, "minute").format(),
        course: this.courses.find((course) => course.id === reservation.course_id),
      };
    });
    CalendarModule.weeklyAdd([]);
    if (this.selectedInitialView === "timeGridWeek") {
      this.createWeeklyStock();
    }
    if (this.selectedInitialView === "dayGridMonth") {
      this.createMonthlyStock();
    }
    this.calendarHeaderActive();
    this.loading = false;
  }

  private prevButton: Element | null = null;
  private nextButton: Element | null = null;
  private dayButton: Element | null = null;
  private weekButton: Element | null = null;
  private monthButton: Element | null = null;

  private calendarHeaderDisabled() {
    this.prevButton?.setAttribute("disabled", "true");
    this.nextButton?.setAttribute("disabled", "true");
    this.dayButton?.setAttribute("disabled", "true");
    this.weekButton?.setAttribute("disabled", "true");
    this.monthButton?.setAttribute("disabled", "true");
  }
  private calendarHeaderActive() {
    this.prevButton?.removeAttribute("disabled");
    this.nextButton?.removeAttribute("disabled");
    this.dayButton?.removeAttribute("disabled");
    this.weekButton?.removeAttribute("disabled");
    this.monthButton?.removeAttribute("disabled");
  }

  private async mounted() {
    this.registerCourseSelectorListeners(); // Backlog#1151
    this.loading = true;
    await this.getCourses();
    this.showChildComponent = true;

    // カレンダーの初期設定を行う
    // this.initializeCalendar();
    if (this.fullCalendar.getApi().currentData.viewApi.type === "timeGridDay") {
      const el = document.getElementsByClassName("fc-view-harness");
      (el[0] as HTMLElement).style.display = "none";
    }
    if (this.selectedCourse) {
      this.prevButton = document.getElementsByClassName("fc-prev-button")[0];
      this.nextButton = document.getElementsByClassName("fc-next-button")[0];
      this.dayButton = document.getElementsByClassName("fc-timeGridDay-button")[0];
      this.weekButton = document.getElementsByClassName("fc-timeGridWeek-button")[0];
      this.monthButton = document.getElementsByClassName("fc-dayGridMonth-button")[0];
      if (this.selectedCourse?.id === "all") {
        this.weekButton?.setAttribute("disabled", "true");
        this.monthButton?.setAttribute("disabled", "true");
        this.dayButton?.setAttribute("disabled", "true");
      }
      this.dayButton.addEventListener("click", () => {
        CalendarModule.changeSelectedInitialView("timeGridDay");
        this.getReservations();
        location.reload();
      });
      this.weekButton.addEventListener("click", () => {
        CalendarModule.changeSelectedInitialView("timeGridWeek");
        this.getReservations();
        location.reload();
      });
      this.monthButton.addEventListener("click", () => {
        CalendarModule.changeSelectedInitialView("dayGridMonth");
        this.getReservations();
        location.reload();
      });
      if (this.selectedCourse?.id !== "all") {
        const res = await FacilityService.getCourseDetail(this.hospitalId, this.selectedCourse.id);
        CalendarModule.changeSelectedCourse(res.data.data[0]);
        this.businessHoursArrayFactory(); // 営業時間設定
        // // holidays を API から取得
        // this.holidays = await this.getMonthsClosedDates();
        // console.log("holidays:", this.holidays);
      }
      this.prevButton.addEventListener("click", () => {
        if (this.selectedCourse?.id !== "all") {
          this.getReservations();
        }
        if (
          this.fullCalendar.getApi().currentData.viewApi.type === "timeGridDay" ||
          this.fullCalendar.getApi().currentData.viewApi.type === "timeGridWeek"
        ) {
          CalendarModule.changeCurrentDate(
            moment(this.fullCalendar.getApi().currentData.viewApi.currentStart).format("YYYY-MM-DD")
          );
          if (this.selectedCourse?.id !== "all") {
            this.setDayViewBusinessHour(); // slotMinTime,slotMaxTimeの設定
            this.businessHoursArrayFactory(); // 営業時間設定
          }
        }
      });
      this.nextButton.addEventListener("click", () => {
        if (this.selectedCourse?.id !== "all") {
          this.getReservations();
        }
        if (
          this.fullCalendar.getApi().currentData.viewApi.type === "timeGridDay" ||
          this.fullCalendar.getApi().currentData.viewApi.type === "timeGridWeek"
        ) {
          CalendarModule.changeCurrentDate(
            moment(this.fullCalendar.getApi().currentData.viewApi.currentStart).format("YYYY-MM-DD")
          );
          if (this.selectedCourse?.id !== "all") {
            this.setDayViewBusinessHour(); // slotMinTime,slotMaxTimeの設定
            this.businessHoursArrayFactory(); // 営業時間設定
          }
        }
      });
      if (this.selectedCourse && this.selectedCourse.id !== "all") {
        if (this.selectedInitialView !== "timeGridDay") {
          await this.setDayViewBusinessHour();
          await this.getReservations();
        }
      }
    }
    this.loading = false;
  }

  private getBusinessHoursInMinutes(
    businessHours: { daysOfWeek: number[]; startTime: string; endTime: string }[]
  ): number {
    // 一番早いstartTimeを取得
    const earliestStartTime = businessHours.reduce((earliest, current) => {
      return current.startTime < earliest ? current.startTime : earliest;
    }, businessHours[0].startTime);

    // 一番遅いendTimeを取得
    const latestEndTime = businessHours.reduce((latest, current) => {
      return current.endTime > latest ? current.endTime : latest;
    }, businessHours[0].endTime);
    // startTimeとendTimeを分に変換
    const [startHour, startMinute] = earliestStartTime.split(":").map(Number);
    const [endHour, endMinute] = latestEndTime.split(":").map(Number);

    // 営業時間を分で計算
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;

    return endTimeInMinutes - startTimeInMinutes;
  }

  private convertTimeToMinutes(time: string): number {
    const [hours, minutes, seconds] = time.split(":").map(Number);
    return hours * 60 + minutes + Math.floor(seconds / 60);
  }

  private displayReservationInfo(item: any) {
    const name = item.customer_last_name ? item.customer_last_name : item.member_info.last_name;
    let petName = item.pet_name ? item.pet_name : "";
    if (!petName) petName = item.patient_name ? item.patient_name : "";
    const nameKana = item.customer_last_name ? item.customer_last_name_kana : item.member_info.last_name_kana;
    let petNameKana = item.pet_name ? item.pet_name_kana : "";
    if (!petNameKana) petNameKana = item.patient_name ? item.patient_name_kana : "";
    let petType = item.pet_animal_type ? item.pet_animal_type : "";
    if (!petType) petType = item.patient_animal_type ? item.patient_animal_type : "";
    return (
      `
      ${item.patient && item.patient.alias_id ? item.patient.alias_id : ""}` +
      "　" +
      `${name ? name : `(${nameKana})`}` +
      "　" +
      "様　" +
      `${petName ? petName : `(${petNameKana})`}` +
      "　" +
      `${petType}`
    );
  }

  private setDayViewBusinessHour() {
    const startTimes: Moment[] = [];
    const endTimes: Moment[] = [];
    const daysOfWeek = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart).weekday();
    let stringDaysOfWeek = "";
    if (daysOfWeek === 0) {
      stringDaysOfWeek = "Sun";
    } else if (daysOfWeek === 1) {
      stringDaysOfWeek = "Mon";
    } else if (daysOfWeek === 2) {
      stringDaysOfWeek = "Tue";
    } else if (daysOfWeek === 3) {
      stringDaysOfWeek = "Wed";
    } else if (daysOfWeek === 4) {
      stringDaysOfWeek = "Thu";
    } else if (daysOfWeek === 5) {
      stringDaysOfWeek = "Fri";
    } else if (daysOfWeek === 6) {
      stringDaysOfWeek = "Sat";
    }
    if (checkHoliday(this.fullCalendar.getApi().currentData.viewApi.currentStart)) stringDaysOfWeek = "Holiday";

    const currentStart = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart);
    const currentEnd = moment(this.fullCalendar.getApi().currentData.viewApi.currentEnd);

    this.selectedCourse?.details.forEach((detail) => {
      // 期間チェック
      if (detail.period && detail.period.start_year && detail.period.start_month && detail.period.start_date) {
        const periodStart = moment({
          year: detail.period.start_year,
          month: detail.period.start_month - 1,
          date: detail.period.start_date,
        });

        // 終了日が設定されている場合のみperiodEndを設定
        let periodEnd: moment.Moment | null = null;
        if (detail.period.end_year && detail.period.end_month && detail.period.end_date) {
          periodEnd = moment({
            year: detail.period.end_year,
            month: detail.period.end_month - 1,
            date: detail.period.end_date,
          });
        }

        // 表示期間と設定期間が重なっているかチェック
        const isPeriodOverlap = periodEnd
          ? currentStart.isSameOrBefore(periodEnd) && currentEnd.isSameOrAfter(periodStart)
          : currentEnd.isSameOrAfter(periodStart);

        if (isPeriodOverlap) {
          detail.days.forEach((day) => {
            if (this.selectedInitialView === "timeGridDay") {
              if (day.available_days.includes(stringDaysOfWeek)) {
                day.times.forEach((time) => {
                  startTimes.push(
                    moment()
                      .set("hour", time.on_time.start_hour)
                      .set("minute", time.on_time.start_minute)
                      .set("second", 0)
                  );
                  endTimes.push(
                    moment().set("hour", time.on_time.end_hour).set("minute", time.on_time.end_minute).set("second", 0)
                  );
                });
              }
            } else if (this.selectedInitialView === "timeGridWeek") {
              day.times.forEach((time) => {
                startTimes.push(
                  moment()
                    .set("hour", time.on_time.start_hour)
                    .set("minute", time.on_time.start_minute)
                    .set("second", 0)
                );
                endTimes.push(
                  moment().set("hour", time.on_time.end_hour).set("minute", time.on_time.end_minute).set("second", 0)
                );
              });
            }
          });
        }
      }
    });

    startTimes.sort((a, b) => moment(a).diff(b));
    endTimes.sort((a, b) => moment(b).diff(a));
    if (startTimes.length > 0 && endTimes.length > 0) {
      if (moment(startTimes[0]).format("HH:mm") != "00:00" && moment(endTimes[0]).format("HH") != "00") {
        this.calendarOptions.views.timeGridWeek = {
          slotMinTime: moment(startTimes[0]).format("HH:mm"),
          slotMaxTime: moment(endTimes[0]).format("HH:mm"),
        };
        this.calendarOptions.views.timeGridDay = {
          slotMinTime: moment(startTimes[0]).format("HH:mm"),
          slotMaxTime: moment(endTimes[0]).format("HH:mm"),
        };
      } else if (moment(startTimes[0]).format("HH:mm") != "00:00" && moment(endTimes[0]).format("HH") == "00") {
        this.calendarOptions.views.timeGridWeek = {
          slotMinTime: moment(startTimes[0]).format("HH:mm"),
        };
        this.calendarOptions.views.timeGridDay = {
          slotMinTime: moment(startTimes[0]).format("HH:mm"),
        };
      }
    }
  }

  private start = ""; //現在表示している日時のスタート
  private end = ""; //現在表示している日時のエンド
  // カレンダーの設定
  private calendarOptions: any = {
    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
    initialView: this.selectedInitialView,
    headerToolbar: {
      center: "prev,title,next",
      right: "timeGridDay,timeGridWeek,dayGridMonth",
      left: "",
    },
    locale: "ja",
    firstDay: this.selectedInitialView === "timeGridWeek" ? new Date().getDay() : 1,
    events: [],
    eventBackgroundColor: "#fff",
    eventBorderColor: "#fff",

    views: {
      // 週表示で表示する時間帯
      timeGridWeek: {
        slotMinTime: "00:00",
        slotMaxTime: "24:00",
      },
      timeGridDay: {
        slotMinTime: "00:00:00",
        slotMaxTime: "24:00:00",
      },
    },
    nowIndicator: true,
    // viewが変わった時に動くイベント
    datesSet: this.handleDatesSet,
    navLinkDayClick: this.clickMonthDay,
    dayCellClassNames: this.dayCellClassNames,
    initialDate: this.currentDate ? this.currentDate : moment(new Date()).format("YYYY-MM-DD"),
    expandRows: true,
    allDaySlot: false,
    navLinks: true,
    slotLabelFormat: {
      hour: "numeric",
      minute: "2-digit",
      omitZeroMinute: false,
      meridiem: "short",
    },
    buttonText: {
      day: "日",
      month: "月",
      week: "週",
      prev: "◀︎",
      next: "▶︎",
    },
    slotDuration: `00:${this.selectedCourse?.duration_minute}:00`,
    slotLabelInterval: this.selectedCourse?.duration_minute == 90 ? "01:30" : "01:00",
    businessHours: this.businessHours,
  };

  private convertDays(days: string[]) {
    const convertedDays = days.map((value) => {
      if (value === "Sun") {
        return 0;
      } else if (value === "Mon") {
        return 1;
      } else if (value === "Tue") {
        return 2;
      } else if (value === "Wed") {
        return 3;
      } else if (value === "Thu") {
        return 4;
      } else if (value === "Fri") {
        return 5;
      } else if (value === "Sat") {
        return 6;
      } else {
        return 99;
      }
    });
    return convertedDays;
  }

  private businessHourStringFactory(hour: number, minute: number, duration?: number) {
    let hours = moment().set("hour", hour).set("minute", minute).format("HH:mm");
    if (duration && hour === 0 && minute === 0) {
      hours = moment().set("hour", hour).set("minute", minute).add(-1, "minute").format("HH:mm");
    } else if (duration) {
      hours = moment().set("hour", hour).set("minute", minute).add(duration, "minute").format("HH:mm");
      if (hours.slice(0, 2) === "00" && hours.slice(3, 5) === "00") {
        hours = "23:59";
      }
    }
    return hours;
  }

  private businessHoursArrayFactory() {
    const businessHoursArray: BusinessHours[] = [];
    const businessHoursForSetHeightCalendar: BusinessHours[] = [];
    const timeSlots = new Map<string, Set<number>>();
    const timeSlotsForSetHeightCalendar = new Map<string, Set<number>>();

    // カレンダーの表示期間を取得
    const calendarStart = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart);
    const calendarEnd = moment(this.fullCalendar.getApi().currentData.viewApi.currentEnd);

    // 表示期間内の日付配列を作成
    const dateArray: string[] = [];
    const currentDate = moment(calendarStart);
    const endDate = moment(calendarEnd);

    while (currentDate.isBefore(endDate)) {
      dateArray.push(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "days");
    }

    // コース設定から利用可能な曜日と時間を取得
    if (this.selectedCourse?.details) {
      for (const detail of this.selectedCourse.details) {
        // 期間チェック
        const periodStart = moment({
          year: detail.period.start_year,
          month: detail.period.start_month - 1,
          date: detail.period.start_date,
        });

        const periodEnd =
          detail.period.end_year && detail.period.end_month && detail.period.end_date
            ? moment({
                year: detail.period.end_year,
                month: detail.period.end_month - 1,
                date: detail.period.end_date,
              })
            : moment({ year: 9999, month: 11, date: 31 });

        // dateArrayの各日付について処理
        for (const date of dateArray) {
          const momentDate = moment(date);
          if (momentDate.isSameOrAfter(periodStart) && momentDate.isSameOrBefore(periodEnd)) {
            for (const day of detail.days) {
              const isHoliday = checkHoliday(momentDate.toDate());
              const dayNumber = momentDate.day();
              const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dayNumber];
              for (const time of day.times) {
                const timeKey = `${String(time.on_time.start_hour).padStart(2, "0")}:${String(
                  time.on_time.start_minute
                ).padStart(2, "0")}-${String(time.on_time.end_hour).padStart(2, "0")}:${String(
                  time.on_time.end_minute
                ).padStart(2, "0")}`;
                if (!timeSlotsForSetHeightCalendar.has(timeKey)) {
                  timeSlotsForSetHeightCalendar.set(timeKey, new Set());
                }
                timeSlotsForSetHeightCalendar.get(timeKey)?.add(dayNumber);
              }

              // 祝日の場合、is_holiday_primary=trueの設定を優先
              if (isHoliday) {
                const holidayPrimarySetting = detail.days.find(
                  (d) => d.is_holiday_primary && d.available_days.includes("Holiday")
                );
                const hasPrimarySetting = detail.days.some((d) => d.is_holiday_primary);

                if (hasPrimarySetting) {
                  // is_holiday_primary=trueの設定があり、かつHolidayが含まれる場合のみ営業
                  if (holidayPrimarySetting) {
                    for (const time of holidayPrimarySetting.times) {
                      const timeKey = `${String(time.on_time.start_hour).padStart(2, "0")}:${String(
                        time.on_time.start_minute
                      ).padStart(2, "0")}-${String(time.on_time.end_hour).padStart(2, "0")}:${String(
                        time.on_time.end_minute
                      ).padStart(2, "0")}`;
                      if (!timeSlots.has(timeKey)) {
                        timeSlots.set(timeKey, new Set());
                      }
                      timeSlots.get(timeKey)?.add(dayNumber);
                    }
                  }
                } else if (day.available_days.includes(dayName)) {
                  // is_holiday_primary=falseで、その曜日が通常営業日として設定されている場合
                  for (const time of day.times) {
                    const timeKey = `${String(time.on_time.start_hour).padStart(2, "0")}:${String(
                      time.on_time.start_minute
                    ).padStart(2, "0")}-${String(time.on_time.end_hour).padStart(2, "0")}:${String(
                      time.on_time.end_minute
                    ).padStart(2, "0")}`;
                    if (!timeSlots.has(timeKey)) {
                      timeSlots.set(timeKey, new Set());
                    }
                    timeSlots.get(timeKey)?.add(dayNumber);
                  }
                }
                continue;
              }

              // 祝日でない場合は通常の曜日設定を使用
              if (day.available_days.includes(dayName)) {
                for (const time of day.times) {
                  const timeKey = `${String(time.on_time.start_hour).padStart(2, "0")}:${String(
                    time.on_time.start_minute
                  ).padStart(2, "0")}-${String(time.on_time.end_hour).padStart(2, "0")}:${String(
                    time.on_time.end_minute
                  ).padStart(2, "0")}`;
                  if (!timeSlots.has(timeKey)) {
                    timeSlots.set(timeKey, new Set());
                  }
                  timeSlots.get(timeKey)?.add(dayNumber);
                }
              }
            }
          }
        }
      }
    }

    // カレンダー高さ設定用のビジネスアワーを作成
    for (const [timeKey, days] of timeSlotsForSetHeightCalendar) {
      if (days.size > 0) {
        const [startTime, endTime] = timeKey.split("-");
        businessHoursForSetHeightCalendar.push({
          daysOfWeek: Array.from(days),
          startTime,
          endTime,
        });
      }
    }

    // 時間枠ごとにビジネスアワーを作成
    for (const [timeKey, days] of timeSlots) {
      if (days.size > 0) {
        const [startTime, endTime] = timeKey.split("-");
        businessHoursArray.push({
          daysOfWeek: Array.from(days),
          startTime,
          endTime,
        });
      }
    }

    // 営業日が1つもない場合のデフォルト値を設定
    if (businessHoursArray.length === 0) {
      businessHoursArray.push({
        daysOfWeek: [],
        startTime: "09:00",
        endTime: "16:00",
      });
    }

    CalendarModule.changeBusinessHours(businessHoursArray);
    CalendarModule.changeBusinessHoursForSetHeightCalendar(businessHoursForSetHeightCalendar);
    if (this.currentDate && !this.isReloaded) {
      location.reload();
      CalendarModule.changeIsReloaded(true);
    } else {
      CalendarModule.changeIsReloaded(false);
    }
  }

  private dayCellClassNames(data: any) {
    if (checkHoliday(data.date)) {
      return ["fc-holiday"];
    }
  }

  private setCalendarHeight(viewType: string) {
    if (viewType === "dayGridMonth") {
      this.calendarOptions.height = "1000px";
      this.calendarOptions.contentHeight = "auto";
    } else if (viewType === "timeGridWeek") {
      const calendarHeight =
        (this.getBusinessHoursInMinutes(this.businessHoursForSetHeightCalendar) /
          this.convertTimeToMinutes(this.calendarOptions.slotDuration)) *
        this.weeklyHeight;
      this.calendarOptions.height = calendarHeight + 300 < 1000 ? "800px" : calendarHeight + 300 + "px";
    } else if (viewType === "timeGridDay") {
      this.calendarOptions.contentHeight = "auto";
      if (this.selectedCourse && this.selectedCourse.duration_minute < 60) {
        this.calendarOptions.height = "1500px";
      } else {
        this.calendarOptions.height = "1000px";
      }
      CalendarModule.changeCurrent("day");
      this.calendarOptions.events = this.currentEvents;
    }
  }

  private handleDatesSet(info: any) {
    // カレンダーから現在表示している日時のスタートを取得
    this.start = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart).format();
    // カレンダーから現在表示している日時のエンドを取得
    this.end = moment(this.fullCalendar.getApi().currentData.viewApi.currentEnd).format();
    if (this.selectedCourse) {
      this.setCalendarHeight(info.view.type);
      this.$nextTick(() => {
        if (this.fullCalendar) {
          this.fullCalendar.getApi().setOption("height", this.calendarOptions.height);
        }
      });
      const nonBusinessArea = document.querySelector(".fc-non-business");
      if (nonBusinessArea) nonBusinessArea.textContent = "診療時間外";
    }
  }

  private async clickMonthDay(info: Date) {
    CalendarModule.changeSelectedInitialView("timeGridDay");
    CalendarModule.changeCurrentDate(moment(info).format("YYYY-MM-DD"));
    location.reload();
  }

  private dateCheckToday(date: string) {
    const argDate = moment(date).format("YYYYMMDD");
    const start = moment(this.start).format("YYYYMMDD");
    return moment(argDate).isSame(start);
  }
  private dateCheckWeeklyOrMonthly(date: string) {
    const argDate = moment(date).format("YYYYMMDD");
    const start = moment(this.start).format("YYYYMMDD");
    const end = moment(this.end).format("YYYYMMDD");
    return moment(argDate).isSameOrAfter(start) && moment(argDate).isBefore(end);
  }

  private displayReservationInfoName(item: any) {
    if (!item.customer && !item.member_info) {
      return "";
    }
    const name = item.customer_last_name ? item.customer_last_name : item.member_info.last_name;
    const nameKana = item.customer_last_name ? item.customer_last_name_kana : item.member_info.last_name_kana;
    return `${name ? name : `(${nameKana})`}`;
  }
  private displayReservationInfoPetName(item: any) {
    let petName = item.pet_name ? item.pet_name : "";
    if (!petName) petName = item.patient_name ? item.patient_name : "";
    let petNameKana = item.pet_name ? item.pet_name_kana : "";
    if (!petNameKana) petNameKana = item.patient_name ? item.patient_name_kana : "";
    return `${petName ? petName : `(${petNameKana})`}`;
  }

  private async getReservationCsvData() {
    if (!this.canClickCalendarButtons) return;
    this.csvDownloading = true;
    try {
      // 子コンポーネントのメソッドを呼び出す
      if (this.$refs.allCourseCalendar) {
        await (this.$refs.allCourseCalendar as any).createOutputCsvReservations();
      }
      const config = {
        delimiter: ",", // 区切り文字
        header: true, // キーをヘッダーとして扱う
        newline: "\r\n", // 改行
      };
      let calendarView = "";
      if (this.dayButton?.classList.contains("fc-button-active")) {
        calendarView = "date";
      } else {
        calendarView = "weekly";
      }
      const jsonDataList: any = [];
      let reservations = [...this.reservations, ...this.groupReservations];
      if (
        this.selectedCourse!.id === "all" ||
        (this.selectedCourse?.id !== "all" && this.selectedInitialView === "timeGridDay")
      ) {
        reservations = this.allCourseReservations;
      }
      // Backlog#1151: モード②（複数選択）の場合は選択コースの予約だけに絞る
      // モード①/②はどちらも selectedCourse.id === "all" になるため、
      // 上の分岐で allCourseReservations が入るが、モード②では選択外コースも含まれてしまう
      // 2026-09-11 定例で確定：選択コースがコース連動している場合、連動コースの予約も含める
      if (this.displayMode === "multi") {
        const targetIds = new Set<string>(this.selectedCourseIds);
        const courseGroupsRes = await FacilityService.getCourseGroups(this.hospitalId);
        const courseGroups = courseGroupsRes.data.data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        courseGroups.forEach((group: any) => {
          const courseIds: string[] = group.course_ids ?? [];
          if (courseIds.some((id) => this.selectedCourseIds.includes(id))) {
            courseIds.forEach((id) => targetIds.add(id));
          }
        });
        reservations = reservations.filter((r: ReservationData) => targetIds.has(r.course_id));
      }
      // CSV出力順: 予約日昇順 → 開始時間昇順 → コース名昇順。
      // 予約一覧画面のソート仕様（予約日昇順→同日内は開始時間昇順）に揃える形。
      // datetime は ISO 形式なので文字列比較で予約日+開始時間の昇順が同時に得られる。
      reservations = [...reservations].sort((a: any, b: any) => {
        const dtCompare = (a.datetime ?? "").localeCompare(b.datetime ?? "");
        if (dtCompare !== 0) return dtCompare;
        const courseA = a.course?.name ?? "";
        const courseB = b.course?.name ?? "";
        return courseA.localeCompare(courseB, "ja");
      });
      reservations.forEach((reservation: any) => {
        if (
          (calendarView == "date" && this.dateCheckToday(reservation.datetime)) ||
          (calendarView == "weekly" && this.dateCheckWeeklyOrMonthly(reservation.datetime))
        ) {
          // 開始日時。予約日は "M月D日"、開始時間は "H:mm"（いずれもゼロ埋めなし、予約一覧CSVと揃える）
          const start = moment(reservation.datetime);
          // 終了時刻の算出。予約固有の duration_min を優先し、無ければ course.duration_minute を使う
          // （AllCourseCalendar.vue の displayTimeDetail と同じロジック）。どちらも無ければ空文字。
          const durationMin =
            reservation.duration_min !== undefined ? reservation.duration_min : reservation.course?.duration_minute;
          const end = durationMin != null ? moment(reservation.datetime).add(durationMin, "minutes") : null;
          const jsonData = {
            予約ID: reservation.id,
            予約日: `${start.month() + 1}月${start.date()}日`,
            コース名: reservation.course?.name ?? "",
            開始時間: `${Number(start.format("H"))}:${start.format("mm")}`,
            終了時間: end ? `${Number(end.format("H"))}:${end.format("mm")}` : "",
            飼い主名: this.displayReservationInfoName(reservation),
            どうぶつ名: this.displayReservationInfoPetName(reservation),
            来院目的: reservation.message,
          };
          // CSVデータ取得
          jsonDataList.push(jsonData);
        }
      });
      // 区切り文字へ変換
      const delimiterString = Papa.unparse(jsonDataList, config);

      // blodへの変換
      const strArray = Encoding.stringToCode(delimiterString);
      const convertedArray = Encoding.convert(strArray, "SJIS", "UNICODE");
      const UintArray = new Uint8Array(convertedArray);
      const blob = new Blob([UintArray], { type: "text/csv" });

      // download
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `予約リスト.csv`;
      link.click();
    } catch (error) {
      // Backlog#1151: 失敗しても読み込み中のまま止まらないよう、必ず finally で解除する
      console.error("getReservationCsvData failed:", error);
    } finally {
      this.csvDownloading = false;
    }
  }

  private async changeAvailableCount(start: string, stock: number, status: string) {
    if (!this.canUpdateCourse) return;
    this.loadingUpdateCourse = true;
    const specialTime = {
      year: 0,
      month: 0,
      date: 0,
      start_hour: 0,
      start_minute: 0,
      available_count: 0,
    };
    if (this.hospitalId && this.selectedCourse) {
      const currentCourse = _.cloneDeep(this.selectedCourse);

      // special_timesの数が2000に達しているかチェック
      const totalSpecialTimes = currentCourse.details.reduce((total, detail) => {
        return total + (detail.special_times?.length || 0);
      }, 0);

      if (totalSpecialTimes >= 2000) {
        this.showModal("delete-special-times");
        this.loadingUpdateCourse = false;
        return;
      }

      specialTime.year = moment(start).get("year");
      specialTime.month = moment(start).get("month") + 1;
      specialTime.date = moment(start).get("date");
      specialTime.start_hour = moment(start).get("hour");
      specialTime.start_minute = moment(start).get("minute");
      // 対象のdetailのIndexを取得してそれに対して処理を行う
      let detailIndex = 0;
      if (currentCourse.details.length > 1) {
        // startの日付がdetailの期間内になっているdetailのindexを取得
        detailIndex = currentCourse.details.findIndex((detail) => {
          let endDate = null;
          if (detail.period.end_year && detail.period.end_month && detail.period.end_date) {
            endDate = moment()
              .set("year", detail.period.end_year)
              .set("month", detail.period.end_month - 1)
              .set("date", detail.period.end_date)
              .set("hour", 23)
              .set("minute", 59)
              .set("second", 59);
          }
          const startDate = moment()
            .set("year", detail.period.start_year)
            .set("month", detail.period.start_month - 1)
            .set("date", detail.period.start_date)
            .set("hour", 0)
            .set("minute", 0)
            .set("second", 0);
          return endDate
            ? moment(start).isBetween(startDate, endDate, null, "[]")
            : moment(start).isSameOrAfter(startDate);
        });
      }
      detailIndex = detailIndex === -1 ? 0 : detailIndex;
      if (currentCourse.details[detailIndex].special_times?.length) {
        const sameSpecialTimeIndex = currentCourse.details[detailIndex].special_times?.findIndex(
          (item) =>
            item.year === specialTime.year &&
            item.month === specialTime.month &&
            item.date === specialTime.date &&
            item.start_hour === specialTime.start_hour &&
            item.start_minute === specialTime.start_minute
        ) as number;
        if (sameSpecialTimeIndex !== -1) {
          // 既に同じ日時の設定がある時
          if (status === "add") {
            (currentCourse.details[detailIndex].special_times as CourseSpecialTime[])[
              sameSpecialTimeIndex
            ].available_count = stock + 1;
          } else {
            (currentCourse.details[detailIndex].special_times as CourseSpecialTime[])[
              sameSpecialTimeIndex
            ].available_count = stock - 1;
          }
        } else {
          // 同じ日時の設定ない時
          if (status === "add") {
            specialTime.available_count = stock + 1;
          } else {
            specialTime.available_count = stock - 1;
          }
          currentCourse.details[detailIndex].special_times?.push(specialTime);
        }
      } else {
        if (status === "add") {
          specialTime.available_count = stock + 1;
          currentCourse.details[detailIndex].special_times = [specialTime];
        } else {
          specialTime.available_count = stock - 1;
          currentCourse.details[detailIndex].special_times = [specialTime];
        }
      }
      const payload = deleteNullProp(currentCourse);
      const res = await FacilityService.updateCourse(this.hospitalId, this.selectedCourse.id, payload);
      CalendarModule.changeSelectedCourse(res.data.data[0]);
      this.createWeeklyStock();
      this.loadingUpdateCourse = false;
    }
  }

  private async getPossibleReservations() {
    try {
      this.loading = true;
      const calendarStartDate = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart);
      const calendarEndDate = moment(this.fullCalendar.getApi().currentData.viewApi.currentEnd);
      const startYear = calendarStartDate.get("year").toString();
      const startMonth = String(calendarStartDate.get("month") + 1);
      const endYear = calendarEndDate.get("year").toString();
      const endMonth = String(calendarEndDate.get("month") + 1);
      if (this.selectedCourse && this.selectedCourse.id) {
        const res = await FacilityService.getPossibleReservations(
          this.hospitalId,
          this.selectedCourse.id,
          startYear,
          startMonth,
          "True"
        );
        this.possibleReservations = res.data.data;
        if (endMonth !== startMonth) {
          const res = await FacilityService.getPossibleReservations(
            this.hospitalId,
            this.selectedCourse.id,
            endYear,
            endMonth,
            "True"
          );
          this.possibleReservations = [...this.possibleReservations, ...res.data.data];
        }
      }
    } catch (error: any) {
      this.loading = false;
      throw new Error(error);
    }
  }

  private weeklyFactory(businessHours: BusinessHours[]) {
    const weekly: WeeklyEvent[] = [];

    // 先にthis.startの曜日に該当するbusinessHourをbusinessHoursの先頭に持ってくる
    const startDay = moment(this.start).day();
    const startBusinessHour = businessHours.find((businessHour) => businessHour.daysOfWeek.includes(startDay));

    if (startBusinessHour) {
      businessHours = [startBusinessHour, ...businessHours.filter((hour) => hour !== startBusinessHour)];
    }
    businessHours.forEach((businessHour) => {
      let weeklyStart = moment(this.start)
        .hour(Number(_.cloneDeep(businessHour).startTime.slice(0, 2)))
        .minute(Number(_.cloneDeep(businessHour).startTime.slice(3, 5)))
        .format();
      // 同じ曜日の営業時間を取得
      const dayTimes = businessHours.filter(
        (value) => JSON.stringify(value.daysOfWeek) === JSON.stringify(businessHour.daysOfWeek)
      );
      dayTimes.sort((a: BusinessHours, b: BusinessHours) => {
        if (a.startTime > b.startTime) return 1;
        return -1;
      });

      for (let i = 1; moment(weeklyStart).isBefore(moment(this.end)); i++) {
        const day = moment(weeklyStart).day();

        const targetDayTimes = dayTimes.filter((value) => value.daysOfWeek.includes(day));
        if (targetDayTimes.length == 0) {
          // 次の日のbusinessHourを探す
          let nextDay = moment(weeklyStart).add(1, "days");
          let nextDayOfWeek = nextDay.day();
          let nextBusinessHour = dayTimes.find((time) => time.daysOfWeek.includes(nextDayOfWeek));

          // 次のbusinessHourが見つかった場合はその時間を設定
          if (nextBusinessHour) {
            weeklyStart = nextDay
              .hour(Number(nextBusinessHour.startTime.slice(0, 2)))
              .minute(Number(nextBusinessHour.startTime.slice(3, 5)))
              .format();
          } else {
            // 見つからない場合は単純に1日進める
            weeklyStart = moment(weeklyStart)
              .add(1, "days")
              .hour(Number(_.cloneDeep(businessHour).startTime.slice(0, 2)))
              .minute(Number(_.cloneDeep(businessHour).startTime.slice(3, 5)))
              .format();
          }
          continue;
        }

        // 現在の時間帯のインデックスを取得
        const currentTimeIndex = targetDayTimes.findIndex((time) => {
          const timeStart = moment(weeklyStart).format("HH:mm");
          return timeStart >= time.startTime && timeStart < time.endTime;
        });

        // 現在の時間帯が見つからない場合は次のループに進む
        if (currentTimeIndex === -1) {
          weeklyStart = moment(weeklyStart).add(this.selectedCourse!.duration_minute, "minutes").format();
          continue;
        }
        // 現在の時間帯の終了時間
        const currentEndTime = moment(weeklyStart)
          .hour(Number(targetDayTimes[currentTimeIndex].endTime.slice(0, 2)))
          .minute(Number(targetDayTimes[currentTimeIndex].endTime.slice(3, 5)))
          .format();

        // 次の時間帯の開始時間（存在する場合）
        const nextStartTime =
          currentTimeIndex < targetDayTimes.length - 1
            ? moment(weeklyStart)
                .hour(Number(targetDayTimes[currentTimeIndex + 1].startTime.slice(0, 2)))
                .minute(Number(targetDayTimes[currentTimeIndex + 1].startTime.slice(3, 5)))
                .format()
            : null;

        const event: WeeklyEvent = {
          title: 0,
          stock: this.selectedCourse ? this.selectedCourse.available_count : 0,
          groupReservedCount: 0,
          start: weeklyStart,
          end: moment(weeklyStart)
            .add(this.selectedCourse!.duration_minute / 60, "hours")
            .format(),
        };
        const duplicateEvent = weekly.find((value) =>
          moment(value.start).isBetween(moment(event.start), moment(event.end), null, "[]")
        );
        if (!duplicateEvent) {
          if (this.selectedCourse?.details) {
            for (const detail of this.selectedCourse.details) {
              if (detail.special_times) {
                const findSpecialTime = detail.special_times.find((item) => {
                  return (
                    moment()
                      .set("year", item.year)
                      .set("month", item.month - 1)
                      .set("date", item.date)
                      .set("hour", item.start_hour)
                      .set("minute", item.start_minute)
                      .set("second", 0)
                      .format() === weeklyStart
                  );
                });
                if (findSpecialTime) {
                  event.stock = findSpecialTime.available_count;
                  break;
                }
              }
            }
          }

          // イベントの追加条件を確認
          const isWithinCurrentTimeSlot = moment(event.start).isBefore(currentEndTime);
          const isValidDuration = moment(event.end).isSameOrBefore(currentEndTime);

          if (isWithinCurrentTimeSlot && isValidDuration) {
            if (this.possibleReservations.length) {
              const possibleReservation = this.possibleReservations.find((reservation: any) => {
                return moment(reservation.datetime).format() === event.start;
              });
              if (possibleReservation) {
                event.title = possibleReservation.available_count - possibleReservation.remained_count;
              }
            }
            weekly.push(event);
          }
        }

        // 次の時間スロットの設定
        if (moment(event.end).isSameOrAfter(currentEndTime)) {
          if (nextStartTime) {
            weeklyStart = nextStartTime;
          } else {
            // 次の日の開始時間を設定
            const nextDay = (day + 1) % 7;
            const nextDayTimes = dayTimes.find((value) => value.daysOfWeek.includes(nextDay));
            if (nextDayTimes) {
              weeklyStart = moment(weeklyStart)
                .add(1, "days")
                .hour(Number(nextDayTimes.startTime.slice(0, 2)))
                .minute(Number(nextDayTimes.startTime.slice(3, 5)))
                .format();
            } else {
              weeklyStart = event.end;
            }
          }
        } else {
          weeklyStart = event.end;
        }
      }
    });
    return weekly;
  }

  private createWeeklyStock() {
    // カレンダーから現在表示している日時のスタートを取得
    this.start = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart).format();
    // カレンダーから現在表示している日時のエンドを取得
    this.end = moment(this.fullCalendar.getApi().currentData.viewApi.currentEnd).format();
    let weekly = [];
    weekly = this.weeklyFactory(this.businessHours);
    CalendarModule.weeklyAdd(weekly);
    CalendarModule.changeCurrent("weekly");

    this.calendarOptions.events = this.currentEvents;
  }

  private async createMonthlyStock() {
    // CalendarModule.monthlyReset();
    let monthly = [];
    // カレンダーから現在表示している日時のスタートを取得
    this.start = moment(this.fullCalendar.getApi().currentData.viewApi.currentStart).format();
    // カレンダーから現在表示している日時のエンドを取得
    this.end = moment(this.fullCalendar.getApi().currentData.viewApi.currentEnd).format();
    const year = moment(this.start).get("year").toString();
    const month = String(moment(this.start).get("month") + 1);
    // 表示しているカレンダーのイベントがあるかどうかチェック
    for (let i = 1; this.end !== this.start; i++) {
      let defaultEvent: MonthlyEvent = {
        title: 0,
        stock: 0,
        start: "",
        groupReservedCount: 0,
      };
      defaultEvent.start = moment(this.start).format("YYYY-MM-DD");
      monthly.push(defaultEvent);
      this.start = moment(this.start).add(1, "days").format();
    }
    const res = await FacilityService.getPossibleReservations(
      this.hospitalId,
      this.selectedCourse!.id,
      year,
      month,
      "True"
    );
    const possibleReservations = res.data.data;
    for (const v of possibleReservations) {
      const d = moment(v.datetime).get("date");
      monthly[d - 1].title = monthly[d - 1].title + (v.available_count - v.remained_count);
      monthly[d - 1].stock = monthly[d - 1].stock + v.available_count;
    }
    CalendarModule.monthlyAdd(monthly);
    CalendarModule.changeCurrent("monthly");
    this.calendarOptions.events = this.currentEvents;
  }

  private deleteLoading = false;
  private selectedMonth = 1;
  private deletedCount = 0;
  private monthOptions = [
    { text: "1ヶ月", value: 1 },
    { text: "3ヶ月", value: 3 },
    { text: "6ヶ月", value: 6 },
  ];

  private get isDeleteDisabled(): boolean {
    if (!this.selectedCourse?.details) return false;

    const targetDate = moment().subtract(this.selectedMonth, "months").startOf("month");

    // 各detailのspecial_timesから対象日付以前のデータをチェック
    return !this.selectedCourse.details.some((detail) => {
      if (!detail.special_times) return false;
      return detail.special_times.some((specialTime) => {
        const specialTimeDate = moment()
          .set("year", specialTime.year)
          .set("month", specialTime.month - 1)
          .set("date", specialTime.date);
        return !specialTimeDate.isAfter(targetDate);
      });
    });
  }

  private async getYearlyClosedDates(): Promise<string[]> {
    const closedDates: string[] = [];
    const today = moment().startOf("day");

    const year = today.format("YYYY");
    const month = today.format("MM");
    const currentCourse = _.cloneDeep(this.selectedCourse);

    const yearlyClosed = await this.getYearlyStockAndClosedDates(year, month);

    for (const dateStr of yearlyClosed) {
      const dateMoment = moment(dateStr, "YYYY-MM-DD");

      // 今日以降のみ追加
      if (!dateMoment.isSameOrAfter(today)) continue;

      const y = dateMoment.year();
      const m = dateMoment.month() + 1;
      const d = dateMoment.date();

      // special_times に available_count=0 があるなら除外
      let hasAvailableSlot = false;
      if (currentCourse && currentCourse.details) {
        hasAvailableSlot = currentCourse.details.some((detail: any) =>
          detail.special_times?.some(
            (st: CourseSpecialTime) => st.year === y && st.month === m && st.date === d && st.available_count === 0
          )
        );
      }

      if (!hasAvailableSlot) {
        closedDates.push(dateStr);
      }
    }
    // console.log("Closed Dates:", closedDates);

    return closedDates;
  }

  private async getYearlyStockAndClosedDates(year: string, month: string): Promise<string[]> {
    const closedDates: string[] = [];
    const monthly: MonthlyEvent[] = [];

    // 対象年の開始・終了日を moment で生成
    const startMoment = moment(`${year}-${month}-01`);
    const endMoment = startMoment.clone().add(1, "year").endOf("month"); // 翌年の月末（forループの終端）

    // 1年分の日付を初期化
    const stockByDate: Record<string, number> = {};
    const current = startMoment.clone();
    while (current.isBefore(endMoment)) {
      const dateStr = current.format("YYYY-MM-DD");
      stockByDate[dateStr] = 0;
      monthly.push({
        title: 0,
        stock: 0,
        start: dateStr,
        groupReservedCount: 0,
      });
      current.add(1, "day");
    }

    // APIから予約可能データ取得
    const res = await FacilityService.getPossibleReservations(
      this.hospitalId,
      this.selectedCourse!.id,
      year,
      month,
      "True",
      "True"
    );
    const reservations = res.data.data;

    // 日別に stock 集計
    for (const r of reservations) {
      const dateStr = moment(r.datetime).format("YYYY-MM-DD");
      if (dateStr in stockByDate) {
        stockByDate[dateStr] += r.available_count;
      }

      // monthly にも反映（Optional）
      const idx = monthly.findIndex((m) => m.start === dateStr);
      if (idx !== -1) {
        monthly[idx].stock += r.available_count;
        monthly[idx].title += r.available_count - r.remained_count;
      }
    }

    // stock = 0 の日を休診日として抽出
    for (const [date, stock] of Object.entries(stockByDate)) {
      if (stock === 0) {
        closedDates.push(date);
      }
    }
    // console.log("Yearly Closed Dates:", closedDates);

    return closedDates;
  }

  private showModal(name: string) {
    this.$modal.show(name);
  }

  private closeModal(name: string) {
    this.$modal.hide(name);
  }

  private showDeleteSpecialTimesModal() {
    this.showModal("delete-special-times");
  }

  private closeDeleteModal() {
    this.closeModal("delete-special-times");
  }

  private showDeleteCompleteModal() {
    this.showModal("delete-complete");
  }

  private closeDeleteCompleteModal() {
    this.closeModal("delete-complete");
  }

  private async deleteSpecialTimes() {
    try {
      this.loadingUpdateCourse = true;
      const currentCourse = _.cloneDeep(this.selectedCourse);

      // 選択した月の1日を取得
      const targetDate = moment().subtract(this.selectedMonth, "months").startOf("month");

      if (!currentCourse) {
        return;
      }

      // S3に保存する削除対象データを収集
      const deletedSpecialTimes: { [key: string]: any[] } = { details: [] };

      // 各detailのspecial_timesから対象日付以前のデータを削除し、削除データを保存
      for (const [index, detail] of currentCourse.details.entries()) {
        if (detail.special_times) {
          const deletedTimes = detail.special_times.filter((specialTime) => {
            const specialTimeDate = moment()
              .set("year", specialTime.year)
              .set("month", specialTime.month - 1)
              .set("date", specialTime.date);
            return !specialTimeDate.isAfter(targetDate);
          });

          if (deletedTimes.length > 0) {
            deletedSpecialTimes.details[index] = deletedTimes;
          }

          // 残すデータで更新
          detail.special_times = detail.special_times.filter((specialTime) => {
            const specialTimeDate = moment()
              .set("year", specialTime.year)
              .set("month", specialTime.month - 1)
              .set("date", specialTime.date);
            return specialTimeDate.isAfter(targetDate);
          });
        }
      }

      // 削除件数を計算
      this.deletedCount = 0;
      deletedSpecialTimes.details.forEach((detail: any) => {
        detail.forEach((item: any) => {
          this.deletedCount += Math.abs(item.available_count - (this.selectedCourse?.available_count || 0));
        });
      });

      // S3に保存するためのJSONデータを作成
      const s3Data = {
        ...currentCourse,
        details: currentCourse.details.map((detail, index) => ({
          ...detail,
          special_times: deletedSpecialTimes.details[index] || [],
        })),
        deleted_at: moment().format(),
        target_date: targetDate.format(),
      };

      // S3用のJSONデータを出力

      await uploadToS3ForCourse(JSON.stringify(s3Data), this.hospitalId, this.selectedCourse!.id);

      // special_timesの配列が空の場合はspecial_timesを削除
      currentCourse.details = currentCourse.details.map((detail) => {
        if (detail.special_times && detail.special_times.length === 0) {
          delete detail.special_times;
        }
        return detail;
      });

      const payload = deleteNullProp(currentCourse);
      // コースデータを更新
      const res = await FacilityService.updateCourse(this.hospitalId, this.selectedCourse!.id, payload);

      this.closeDeleteModal();
      this.showDeleteCompleteModal();
      CalendarModule.changeSelectedCourse(res.data.data[0]);
      this.createWeeklyStock();
      this.loadingUpdateCourse = false;
    } catch (error) {
      console.error("Error deleting special times:", error);
    } finally {
      this.deleteLoading = false;
    }
  }
}
